// src/pages/PlanesAdmin.js
import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import api from '../services/api';
import { toast } from 'react-toastify';

const PlanesAdmin = () => {
  const [planes, setPlanes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    precio_mensual: '',
    max_usuarios: 1,
    max_clientes: 100,
    max_servicios: 10,
    max_productos: 0,
    tiene_sucursales: false,
    tiene_profesionales: false,
    max_profesionales: 0,
    tiene_reportes_avanzados: false,
    activo: 1
  });

  const [confirmModal, setConfirmModal] = useState({ show: false, id: null, nombre: '' });
  const [confirmAction, setConfirmAction] = useState(null);

  useEffect(() => {
    fetchPlanes();
  }, []);

  const fetchPlanes = async () => {
    try {
      const { data } = await api.get('/planes/admin/all');
      setPlanes(data);
    } catch (error) {
      console.error('Error al cargar planes:', error);
      toast.error('Error al cargar planes');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validaciones
    if (!formData.nombre || formData.precio_mensual === '' || formData.precio_mensual === null || formData.precio_mensual === undefined) {
      toast.error('Nombre y precio son requeridos');
      return;
    }
    
    try {
      if (editando) {
        await api.put(`/planes/admin/${editando}`, formData);
        toast.success('✅ Plan actualizado correctamente');
      } else {
        await api.post('/planes/admin', formData);
        toast.success('✅ Plan creado correctamente');
      }
      setShowModal(false);
      fetchPlanes();
      resetForm();
    } catch (error) {
      console.error('Error al guardar plan:', error);
      toast.error(error.response?.data?.error || 'Error al guardar plan');
    }
  };

  const resetForm = () => {
    setEditando(null);
    setFormData({
      nombre: '',
      descripcion: '',
      precio_mensual: '',
      max_usuarios: 1,
      max_clientes: 100,
      max_servicios: 10,
      max_productos: 0,
      tiene_sucursales: false,
      tiene_profesionales: false,
      max_profesionales: 0,
      tiene_reportes_avanzados: false,
      activo: 1
    });
  };

  const editarPlan = (plan) => {
    setEditando(plan.id);
    setFormData({
      nombre: plan.nombre,
      descripcion: plan.descripcion || '',
      precio_mensual: plan.precio_mensual,
      max_usuarios: plan.max_usuarios || 1,
      max_clientes: plan.max_clientes || 100,
      max_servicios: plan.max_servicios || 10,
      max_productos: plan.max_productos || 0,
      tiene_sucursales: plan.tiene_sucursales === 1,
      tiene_profesionales: plan.tiene_profesionales === 1,
      max_profesionales: plan.max_profesionales || 0,
      tiene_reportes_avanzados: plan.tiene_reportes_avanzados === 1,
      activo: plan.activo
    });
    setShowModal(true);
  };

  const toggleActivo = (id, activo) => {
    setConfirmAction({ onConfirm: async () => {
      try {
        await api.put(`/planes/admin/${id}`, { activo: activo ? 0 : 1 });
        toast.success(activo ? '⛔ Plan desactivado' : '✅ Plan activado');
        fetchPlanes();
      } catch (error) {
        console.error('Error al actualizar:', error);
        toast.error('Error al actualizar el plan');
      }
    }});
  };

  const eliminarPlan = async (id, nombre) => {
    setConfirmModal({ show: true, id, nombre });
  };

  const confirmarEliminar = async () => {
    const { id, nombre } = confirmModal;
    setConfirmModal({ show: false, id: null, nombre: '' });
    try {
      await api.delete(`/planes/admin/${id}`);
      toast.success(`🗑️ Plan "${nombre}" eliminado`);
      fetchPlanes();
    } catch (error) {
      console.error('Error al eliminar plan:', error);
      toast.error(error.response?.data?.error || 'Error al eliminar el plan');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="loading-screen">
          <div className="loading"></div>
          <p>Cargando planes...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="admin-dashboard">
        <div className="page-header">
          <div>
            <h2>📦 Gestión de Planes</h2>
            <p className="page-subtitle">Crea, edita y administra los planes de suscripción</p>
          </div>
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            + Nuevo Plan
          </button>
        </div>

        <div className="card">
          <div className="card-title">📋 Lista de Planes</div>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Precio</th>
                  <th>Usuarios</th>
                  <th>Clientes</th>
                  <th>Servicios</th>
                  <th>Profesionales</th>
                  <th>Sucursales</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {planes.map(plan => (
                  <tr key={plan.id}>
                    <td><strong>{plan.nombre}</strong><br/><small>{plan.descripcion?.substring(0, 50)}</small></td>
                    <td>${plan.precio_mensual}/mes</td>
                    <td>{plan.max_usuarios}</td>
                    <td>{plan.max_clientes}</td>
                    <td>{plan.max_servicios}</td>
                    <td>{plan.max_profesionales || 0}</td>
                    <td>{plan.tiene_sucursales ? '✅ Sí' : '❌ No'}</td>
                    <td>
                      <span className={`badge ${plan.activo ? 'badge-success' : 'badge-danger'}`}>
                        {plan.activo ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => editarPlan(plan)}
                          title="Editar plan"
                          style={{ padding: '4px 8px', fontSize: '0.75rem' }}
                        >
                          ✏️
                        </button>
                        <button
                          className="btn btn-sm btn-warning"
                          onClick={() => toggleActivo(plan.id, plan.activo)}
                          title={plan.activo ? 'Desactivar plan' : 'Activar plan'}
                          style={{ padding: '4px 8px', fontSize: '0.75rem' }}
                        >
                          {plan.activo ? '⏸' : '✓'}
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => eliminarPlan(plan.id, plan.nombre)}
                          title="Eliminar plan permanentemente"
                          style={{ padding: '4px 8px', fontSize: '0.75rem' }}
                        >
                          ✕
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Modal de crear/editar plan */}
        {showModal && (
          <div className="modal-overlay">
            <div className="modal modal-lg" onClick={e => e.stopPropagation()} style={{ borderRadius: '16px', overflow: 'hidden' }}>
              <div style={{
                background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
                padding: '1.5rem 1.75rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <div style={{
                    background: 'rgba(255,255,255,0.2)',
                    borderRadius: '10px',
                    width: '42px', height: '42px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '1.3rem',
                  }}>
                    📦
                  </div>
                  <div>
                    <h3 style={{ margin: 0, color: 'white', fontSize: '1.15rem', fontWeight: '700' }}>
                      {editando ? 'Editar Plan' : 'Nuevo Plan'}
                    </h3>
                    <p style={{ margin: 0, color: 'rgba(255,255,255,0.75)', fontSize: '0.8rem' }}>
                      {editando ? 'Modifica la información del plan' : 'Completa los datos para agregar un nuevo plan'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => { setShowModal(false); resetForm(); }}
                  style={{
                    background: 'rgba(255,255,255,0.15)',
                    border: 'none',
                    borderRadius: '8px',
                    color: 'white',
                    width: '32px', height: '32px',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >✕</button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="form-grid">
                    {/* Nombre del Plan */}
                    <div className="form-group full-width">
                      <label>Nombre del Plan *</label>
                      <input
                        type="text"
                        className="form-input"
                        value={formData.nombre}
                        onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                        placeholder="Ej: Básico, Profesional, Empresarial"
                        required
                      />
                    </div>

                    {/* Descripción */}
                    <div className="form-group full-width">
                      <label>Descripción</label>
                      <textarea
                        className="form-textarea"
                        rows="2"
                        value={formData.descripcion}
                        onChange={(e) => setFormData({...formData, descripcion: e.target.value})}
                        placeholder="Describe las características del plan..."
                      />
                    </div>

                    {/* Precio */}
                    <div className="form-group">
                      <label>Precio Mensual (USD) *</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className="form-input"
                        value={formData.precio_mensual}
                        onChange={(e) => setFormData({...formData, precio_mensual: e.target.value})}
                        placeholder="0.00"
                        required
                      />
                    </div>

                    {/* Estado Activo */}
                    <div className="form-group">
                      <label>Estado</label>
                      <select
                        className="form-select"
                        value={formData.activo}
                        onChange={(e) => setFormData({...formData, activo: parseInt(e.target.value)})}
                      >
                        <option value={1}>Activo</option>
                        <option value={0}>Inactivo</option>
                      </select>
                    </div>

                    {/* Límites */}
                    <div className="form-group">
                      <label>Máx. Usuarios</label>
                      <input
                        type="number"
                        className="form-input"
                        value={formData.max_usuarios}
                        onChange={(e) => setFormData({...formData, max_usuarios: parseInt(e.target.value)})}
                        min="1"
                      />
                    </div>

                    <div className="form-group">
                      <label>Máx. Clientes</label>
                      <input
                        type="number"
                        className="form-input"
                        value={formData.max_clientes}
                        onChange={(e) => setFormData({...formData, max_clientes: parseInt(e.target.value)})}
                        min="1"
                      />
                    </div>

                    <div className="form-group">
                      <label>Máx. Servicios</label>
                      <input
                        type="number"
                        className="form-input"
                        value={formData.max_servicios}
                        onChange={(e) => setFormData({...formData, max_servicios: parseInt(e.target.value)})}
                        min="1"
                      />
                    </div>

                    <div className="form-group">
                      <label>Máx. Productos</label>
                      <input
                        type="number"
                        className="form-input"
                        value={formData.max_productos}
                        onChange={(e) => setFormData({...formData, max_productos: parseInt(e.target.value)})}
                        min="0"
                      />
                    </div>

                    <div className="form-group">
                      <label>Máx. Profesionales</label>
                      <input
                        type="number"
                        className="form-input"
                        value={formData.max_profesionales}
                        onChange={(e) => setFormData({...formData, max_profesionales: parseInt(e.target.value)})}
                        min="0"
                      />
                    </div>

                    {/* Características especiales */}
                    <div className="form-group checkbox-group">
                      <label>
                        <input
                          type="checkbox"
                          checked={formData.tiene_sucursales}
                          onChange={(e) => setFormData({...formData, tiene_sucursales: e.target.checked})}
                        />
                        🏢 Multi-sucursal
                      </label>
                    </div>

                    <div className="form-group checkbox-group">
                      <label>
                        <input
                          type="checkbox"
                          checked={formData.tiene_profesionales}
                          onChange={(e) => setFormData({...formData, tiene_profesionales: e.target.checked})}
                        />
                        👥 Gestión de Profesionales
                      </label>
                    </div>

                    <div className="form-group checkbox-group">
                      <label>
                        <input
                          type="checkbox"
                          checked={formData.tiene_reportes_avanzados}
                          onChange={(e) => setFormData({...formData, tiene_reportes_avanzados: e.target.checked})}
                        />
                        📊 Reportes Avanzados
                      </label>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => { setShowModal(false); resetForm(); }}>
                    Cancelar
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {editando ? 'Actualizar Plan' : 'Crear Plan'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
        {/* Modal de confirmar eliminación */}
        {confirmModal.show && (
          <div className="modal-overlay">
            <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 420, borderRadius: '16px', overflow: 'hidden' }}>
              <div style={{
                background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
                padding: '1.25rem 1.5rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                  <span style={{ fontSize: '1.2rem' }}>⚠️</span>
                  <h3 style={{ margin: 0, color: 'white', fontSize: '1rem', fontWeight: '700' }}>
                    Confirmar acción
                  </h3>
                </div>
                <button
                  onClick={() => setConfirmModal({ show: false, id: null, nombre: '' })}
                  style={{
                    background: 'rgba(255,255,255,0.15)',
                    border: 'none',
                    borderRadius: '8px',
                    color: 'white',
                    width: '28px', height: '28px',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >✕</button>
              </div>
              <div className="modal-body" style={{ textAlign: 'center', padding: '2rem 1.5rem' }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚠️</div>
                <p style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                  ¿Eliminar el plan <span style={{ color: '#dc2626' }}>"{confirmModal.nombre}"</span>?
                </p>
                <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary, #6b7280)' }}>
                  Esta acción es permanente y no se puede deshacer.
                </p>
              </div>
              <div className="modal-footer" style={{ justifyContent: 'center', gap: '1rem' }}>
                <button
                  className="btn btn-secondary"
                  onClick={() => setConfirmModal({ show: false, id: null, nombre: '' })}
                >
                  Cancelar
                </button>
                <button
                  className="btn btn-danger"
                  onClick={confirmarEliminar}
                >
                  🗑️ Sí, eliminar
                </button>
              </div>
            </div>
          </div>
        )}

      </div>

      {confirmAction && (
        <div className="modal-overlay">
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '420px', borderRadius: '16px', overflow: 'hidden' }}>
            <div style={{
              background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
              padding: '1.25rem 1.5rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                <span style={{ fontSize: '1.2rem' }}>⚠️</span>
                <h3 style={{ margin: 0, color: 'white', fontSize: '1rem', fontWeight: '700' }}>
                  Confirmar acción
                </h3>
              </div>
              <button
                onClick={() => setConfirmAction(null)}
                style={{
                  background: 'rgba(255,255,255,0.15)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  width: '28px', height: '28px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >✕</button>
            </div>
            <div className="modal-body">
              <p style={{ color: '#374151' }}>¿Confirmas esta acción?</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setConfirmAction(null)}>No</button>
              <button className="btn btn-primary" onClick={() => { confirmAction.onConfirm(); setConfirmAction(null); }}>Sí</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default PlanesAdmin;
